import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Checkbox } from 'designSystem/components/Checkbox'
import { KNOB_HELP } from 'features/timeSeries/experiment/knobCopy'
import { KnobHelp } from 'features/timeSeries/experiment/KnobHelp'
import {
  SplitChunk,
  SplitRole,
  SplitSpec,
  SplitUnit,
} from 'features/timeSeries/experiment/types'
import { FC } from 'react'
import styled from 'styled-components'
import { mobileInputTextStyles } from 'styles/mobileInputTextStyles'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Label = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
`

const Select = styled.select`
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
  ${mobileInputTextStyles}
`

const Input = styled.input`
  width: 4.5rem;
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
  ${mobileInputTextStyles}
`

const DateInput = styled(Input)`
  width: 9rem;
`

const Timeline = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`

const Chunk = styled.button<{ $role: string }>`
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 4px;
  padding: 0.35rem 0.45rem;
  font-size: 0.7rem;
  cursor: pointer;
  min-width: 3.5rem;
  background: ${({ $role, theme }) =>
    $role === 'train'
      ? 'rgba(34, 211, 238, 0.18)'
      : $role === 'val'
        ? 'rgba(249, 115, 22, 0.2)'
        : $role === 'test'
          ? 'rgba(239, 68, 68, 0.2)'
          : theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
`

const Legend = styled.div`
  display: flex;
  gap: 0.75rem;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const CheckRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  cursor: pointer;
`

const nextRole = (role: string): SplitRole => {
  if (role === 'train') return 'val'
  if (role === 'val') return 'test'
  return 'train'
}

/** Units whose chunks the backend can tail-split (chunk_tail_frac). */
const TAIL_UNITS: readonly SplitUnit[] = ['month', 'year', 'days']

type CustomRange = NonNullable<SplitSpec['custom_ranges']>[number]

/** Backend chunk id for a custom range, positional (see build_split_roles). */
const rangeChunkId = (index: number) => `range_${index}`

const addDays = (date: string, days: number): string => {
  // eslint-disable-next-line lingui/no-unlocalized-strings -- ISO timestamp syntax, not user-facing copy.
  const ms = Date.parse(`${date}T00:00:00Z`)
  if (!Number.isFinite(ms)) return date
  return new Date(ms + days * 86_400_000).toISOString().slice(0, 10)
}

/**
 * Seed train/test ranges covering the experiment window, cut at 90% of its
 * span, so switching to custom starts from something runnable.
 */
const seedRanges = (start?: string, end?: string): CustomRange[] => {
  if (!start || !end) return []
  // eslint-disable-next-line lingui/no-unlocalized-strings -- ISO timestamp syntax, not user-facing copy.
  const from = Date.parse(`${start}T00:00:00Z`)
  // eslint-disable-next-line lingui/no-unlocalized-strings -- ISO timestamp syntax, not user-facing copy.
  const to = Date.parse(`${end}T00:00:00Z`)
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return []
  const cut = new Date(from + (to - from) * 0.9).toISOString().slice(0, 10)
  return [
    { role: 'train', start, end: cut },
    { role: 'test', start: cut, end: addDays(end, 1) },
  ]
}

export interface SplitBuilderProps {
  split: SplitSpec
  chunks: SplitChunk[]
  /** Experiment window (YYYY-MM-DD), used to seed custom ranges. */
  windowStart?: string
  windowEnd?: string
  onChange: (split: SplitSpec) => void
}

export const SplitBuilder: FC<SplitBuilderProps> = ({
  split,
  chunks,
  windowStart,
  windowEnd,
  onChange,
}) => {
  const { t } = useLingui()

  const tailOn = typeof split.chunk_tail_frac === 'number'
  const ranges = split.custom_ranges || []

  const setRanges = (next: CustomRange[]) =>
    onChange({
      ...split,
      custom_ranges: next.map((r) => ({
        ...r,
        label: `${r.role || 'train'} ${r.start} → ${r.end}`,
      })),
    })

  const patchRange = (index: number, patch: Partial<CustomRange>) =>
    setRanges(ranges.map((r, i) => (i === index ? { ...r, ...patch } : r)))

  const setUnit = (unit: SplitUnit) => {
    // Chunk ids change with the unit, so assignments are stale; the tail
    // fraction only means something for month/year/days chunking.
    const next: SplitSpec = { ...split, unit, assignments: {} }
    if (!TAIL_UNITS.includes(unit)) delete next.chunk_tail_frac
    if (unit === 'custom' && !ranges.length) {
      next.custom_ranges = seedRanges(windowStart, windowEnd)
    }
    onChange(next)
  }

  const cycleChunk = (id: string, current: string) => {
    if (split.unit === 'custom') {
      // Custom ranges carry their own role; assignments would shadow it.
      const index = ranges.findIndex((_, i) => rangeChunkId(i) === id)
      if (index >= 0) patchRange(index, { role: nextRole(current) })
      return
    }
    const assignments = { ...(split.assignments || {}) }
    if (tailOn && current === 'test') {
      // In tail mode the cycle ends by clearing the explicit role, handing
      // the chunk back to the per-chunk tail split (role becomes "mixed").
      delete assignments[id]
      onChange({ ...split, assignments })
      return
    }
    assignments[id] = nextRole(current)
    onChange({ ...split, assignments })
  }

  const setTailEnabled = (on: boolean) => {
    const next: SplitSpec = { ...split }
    if (on) {
      next.chunk_tail_frac = split.chunk_tail_frac ?? 0.1
    } else {
      delete next.chunk_tail_frac
    }
    onChange(next)
  }

  const setTailPct = (pct: number) => {
    if (!Number.isFinite(pct)) return
    // Backend rejects values outside (0, 1); clamp the percent input.
    const frac = Math.min(Math.max(pct / 100, 0.01), 0.99)
    onChange({ ...split, chunk_tail_frac: frac })
  }

  const applyPreset = (kind: 'tail' | 'alt_months' | 'year_holdout') => {
    if (kind === 'tail') {
      onChange({
        unit: 'tail_frac',
        train_frac: 0.9,
        assignments: { head: 'train', tail: 'test' },
      })
      return
    }
    if (kind === 'alt_months') {
      const assignments: Record<string, string> = {}
      chunks.forEach((c, i) => {
        assignments[c.id] = i % 3 === 2 ? 'test' : i % 3 === 1 ? 'val' : 'train'
      })
      onChange({ unit: 'month', assignments })
      return
    }
    // year_holdout: last year = test, previous = val, rest train
    const assignments: Record<string, string> = {}
    chunks.forEach((c, i) => {
      if (i === chunks.length - 1) assignments[c.id] = 'test'
      else if (i === chunks.length - 2) assignments[c.id] = 'val'
      else assignments[c.id] = 'train'
    })
    onChange({ unit: 'year', assignments })
  }

  return (
    <Wrap>
      <Label>
        <Trans>
          Train / val / test — click a chunk to cycle roles. Train fits; val
          calibrates the band; test is held out for metrics.
        </Trans>
      </Label>
      <Row>
        <KnobHelp label={t`Chunk unit`} help={KNOB_HELP.splitUnit} />
        <Select
          value={split.unit}
          onChange={(e) => setUnit(e.target.value as SplitUnit)}>
          <option value='tail_frac'>Tail fraction (prod default)</option>
          <option value='month'>By month</option>
          <option value='year'>By year</option>
          <option value='days'>By N days</option>
          <option value='custom'>Custom date ranges</option>
        </Select>
        {split.unit === 'tail_frac' && (
          <>
            <KnobHelp label={t`train frac`} help={KNOB_HELP.trainFrac} />
            <Input
              type='number'
              min={0.5}
              max={0.99}
              step={0.01}
              value={split.train_frac ?? 0.9}
              onChange={(e) =>
                onChange({
                  ...split,
                  train_frac: Number(e.target.value),
                })
              }
            />
          </>
        )}
        {split.unit === 'days' && (
          <>
            <Label>days</Label>
            <Input
              type='number'
              min={7}
              value={split.days ?? 30}
              onChange={(e) =>
                onChange({ ...split, days: Number(e.target.value) })
              }
            />
          </>
        )}
      </Row>
      {split.unit === 'custom' && (
        <>
          <Label>
            <Trans>
              Pick the exact dates for each period. Ranges run from 00:00 on the
              start date up to (not including) 00:00 on the end date, so set one
              range&apos;s end to the next one&apos;s start to butt them
              together. Rows outside every range are dropped from both fitting
              and scoring — useful for cutting an outage out of the middle.
            </Trans>
          </Label>
          {!ranges.length && (
            <Label>
              <Trans>
                No ranges yet — set the date window above, then press “Reset to
                window”. Running with an empty list ignores the custom dates.
              </Trans>
            </Label>
          )}
          {ranges.map((r, i) => {
            const chunk = chunks.find((c) => c.id === rangeChunkId(i))
            return (
              <Row key={rangeChunkId(i)}>
                <Select
                  value={r.role || 'train'}
                  onChange={(e) => patchRange(i, { role: e.target.value })}>
                  <option value='train'>train</option>
                  <option value='val'>val</option>
                  <option value='test'>test</option>
                  <option value='exclude'>exclude</option>
                </Select>
                <DateInput
                  type='date'
                  value={r.start.slice(0, 10)}
                  onChange={(e) => patchRange(i, { start: e.target.value })}
                />
                <Label>→</Label>
                <DateInput
                  type='date'
                  value={r.end.slice(0, 10)}
                  onChange={(e) => patchRange(i, { end: e.target.value })}
                />
                <Label>{chunk ? `${chunk.rows} rows` : t`run a preview`}</Label>
                <Button
                  variant='outline'
                  // An empty range list makes the backend fall back to N-day
                  // chunking, so the last range stays put.
                  disabled={ranges.length < 2}
                  onClick={() =>
                    setRanges(ranges.filter((_, idx) => idx !== i))
                  }>
                  <Trans>Remove</Trans>
                </Button>
              </Row>
            )
          })}
          <Row>
            <Button
              variant='outline'
              onClick={() =>
                setRanges([
                  ...ranges,
                  {
                    role: 'test',
                    start: ranges.at(-1)?.end || windowStart || '',
                    end: windowEnd ? addDays(windowEnd, 1) : '',
                  },
                ])
              }>
              <Trans>Add range</Trans>
            </Button>
            <Button
              variant='outline'
              onClick={() => setRanges(seedRanges(windowStart, windowEnd))}>
              <Trans>Reset to window (90 / 10)</Trans>
            </Button>
          </Row>
        </>
      )}
      {TAIL_UNITS.includes(split.unit) && (
        <Row>
          <CheckRow>
            <Checkbox
              checked={tailOn}
              onCheckedChange={(v) => setTailEnabled(!!v)}
            />
            <KnobHelp
              label={t`Tail-split each chunk`}
              help={KNOB_HELP.chunkTailFrac}
            />
          </CheckRow>
          {tailOn && (
            <>
              <Label>last</Label>
              <Input
                type='number'
                min={1}
                max={99}
                step={1}
                value={Math.round((split.chunk_tail_frac ?? 0.1) * 100)}
                onChange={(e) => setTailPct(Number(e.target.value))}
              />
              <Label>
                <Trans>% of each un-assigned chunk → test</Trans>
              </Label>
            </>
          )}
        </Row>
      )}
      {tailOn && (
        <Label>
          <Trans>Un-assigned chunks split in time: their last</Trans>{' '}
          {Math.round((split.chunk_tail_frac ?? 0.1) * 100)}
          <Trans>
            % of rows become test, the rest train (shown as “mixed”). Clicking a
            chunk still assigns it a whole-chunk role; click past test to hand
            it back to the tail split.
          </Trans>
        </Label>
      )}
      <Row>
        <Button variant='outline' onClick={() => applyPreset('tail')}>
          <Trans>Preset: tail 10%</Trans>
        </Button>
        <Button variant='outline' onClick={() => applyPreset('alt_months')}>
          <Trans>Preset: alt months</Trans>
        </Button>
        <Button variant='outline' onClick={() => applyPreset('year_holdout')}>
          <Trans>Preset: year holdout</Trans>
        </Button>
      </Row>
      <Legend>
        <span>
          <Trans>train (cyan)</Trans>
        </span>
        <span>
          <Trans>val (orange)</Trans>
        </span>
        <span>
          <Trans>test (red)</Trans>
        </span>
        <span>
          <Trans>mixed (cyan→red tail split)</Trans>
        </span>
      </Legend>
      <Timeline>
        {chunks.map((c) => {
          const isMixed = c.role === 'mixed'
          const trainPct =
            isMixed && c.rows > 0
              ? Math.round(((c.train_rows ?? 0) / c.rows) * 100)
              : 0
          return (
            <Chunk
              key={c.id}
              type='button'
              $role={c.role}
              style={
                isMixed
                  ? {
                      background: `linear-gradient(90deg, rgba(34, 211, 238, 0.18) 0%, rgba(34, 211, 238, 0.18) ${trainPct}%, rgba(239, 68, 68, 0.2) ${trainPct}%)`,
                    }
                  : undefined
              }
              title={
                isMixed
                  ? t`${{ value456_25: c.label }}: ${{ value456_37: c.rows }} rows (${{ value456_53: c.train_rows ?? 0 }} train / ${{ value456_82: c.test_rows ?? 0 }} test)\n${{ value456_109: c.start ?? '' }} → ${{ value456_128: c.end ?? '' }}`
                  : t`${{ value457_25: c.label }}: ${{ value457_37: c.rows }} rows\n${{ value457_53: c.start ?? '' }} → ${{ value457_72: c.end ?? '' }}`
              }
              onClick={() => cycleChunk(c.id, c.role)}>
              <div>{c.label}</div>
              <div>
                {isMixed
                  ? t`${{ value463_25: c.train_rows ?? 0 }} train / ${{ value463_54: c.test_rows ?? 0 }} test`
                  : `${c.role} · ${c.rows}`}
              </div>
            </Chunk>
          )
        })}
        {chunks.length === 0 && (
          <Label>
            <Trans>Chunks appear after a data preview loads.</Trans>
          </Label>
        )}
      </Timeline>
    </Wrap>
  )
}
